import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { DialogClose } from "@radix-ui/react-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useGetQueryRulesQuery,
  useUpdateQueryRulesMutation,
} from "@/services/it/updates/rules/query-rules";
import { Load } from "components/load";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Logs } from "lucide-react";
import { RulesLogModal } from "./rules-log-modal";

const Schema = z.object({
  auto_approval: z.number().min(1, "Deve ser pelo menos 1."),
  max_query: z.number().min(1, "Deve ser pelo menos 1."),
});

type FormData = z.infer<typeof Schema>;

export function SettingsModal() {
  const [open, setOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [formData, setFormData] = React.useState<FormData | null>(null);

  const { data: rules, isLoading } = useGetQueryRulesQuery({
    search: "",
    orderBy: JSON.stringify({}),
    perPage: 1,
    meta: { page: 1 },
  });

  const [updateRules, { isLoading: isUpdating }] =
    useUpdateQueryRulesMutation();

  const form = useForm<FormData>({
    resolver: zodResolver(Schema),
    defaultValues: {
      auto_approval: rules?.data[0]?.auto_approval || 1,
      max_query: rules?.data[0]?.max_query || 1,
    },
  });

  React.useEffect(() => {
    if (rules?.data?.length) {
      form.reset({
        auto_approval: rules.data[0].auto_approval || 1,
        max_query: rules.data[0].max_query || 1,
      });
    }
  }, [rules, form]);

  const onSubmit = async (data: FormData) => {
    setFormData(data);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (formData) {
      try {
        await updateRules(formData).unwrap();
        console.log("Regras atualizadas com sucesso.");
        setOpen(false);
        setConfirmOpen(false);
      } catch (error) {
        console.error("Erro ao atualizar regras:", error);
      }
    }
  };

  if (isLoading) return <Load />;

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <div className="border border-black/20 hover:bg-black/5 transition-all ease-in-out duration-300 rounded-full p-2 cursor-pointer">
            <Logs size={14} />
          </div>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[665px] bg-white">
          <DialogHeader className="w-full flex items-start justify-between flex-row py-2">
            <div>
              <DialogTitle>Regras de Aprovação</DialogTitle>
              <DialogDescription>
                Configure e altere as regras para aprovação automática.
              </DialogDescription>
            </div>
            <RulesLogModal />
          </DialogHeader>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-5"
          >
            <div className="space-y-4">
              <div>
                <Label
                  htmlFor="auto_approval"
                  className="flex items-center gap-2 mb-1"
                >
                  Linhas máximas para aprovação automática
                </Label>
                <Input
                  id="auto_approval"
                  type="number"
                  {...form.register("auto_approval", { valueAsNumber: true })}
                />
                {form.formState.errors.auto_approval && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors.auto_approval.message}
                  </p>
                )}
              </div>
              <div>
                <Label
                  htmlFor="max_query"
                  className="flex flex-col items-start lg:flex-row lg:items-center gap-2 mb-1 flex-wrap"
                >
                  Linhas máximas que uma query pode afetar
                  <p className="text-sm text-gray-500">
                    (Caso passe desse número, a query será rejeitada)
                  </p>
                </Label>
                <Input
                  id="max_query"
                  type="number"
                  {...form.register("max_query", { valueAsNumber: true })}
                />
                {form.formState.errors.max_query && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors.max_query.message}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter className="flex items-center justify-between">
              <DialogClose asChild>
                <Button
                  type="button"
                  className="bg-transparent border border-black/20 text-black rounded-md font-semibold text-sm p-2 px-4 flex items-center gap-2 hover:bg-black/10"
                >
                  Cancelar
                </Button>
              </DialogClose>
              <Button
                type="submit"
                disabled={isUpdating}
                className="bg-[#FFCC32] text-black rounded-md font-semibold text-sm p-2 px-4 flex items-center gap-2 hover:[#FFCC32]/80"
              >
                {isUpdating ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-[425px] bg-white">
          <DialogHeader>
            <DialogTitle>Confirmar Alterações</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja mudar as regras de aprovação?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => setConfirmOpen(false)}
              className="bg-transparent border border-black/20 text-black rounded-md font-semibold text-sm p-2 px-4 flex items-center gap-2 hover:bg-black/10"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={isUpdating}
              className="bg-[#FFCC32] text-black rounded-md font-semibold text-sm p-2 px-4 flex items-center gap-2 hover:[#FFCC32]/80"
            >
              {isUpdating ? "Confirmando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
